import { useMemo, useState, useCallback } from 'react';
import { Flex, Text, Box, Table, TablePagination, CellText, type ColumnConfig } from '@backstage/ui';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import type { DbQueryResponse } from './db-explorer.types';
import styles from './db-explorer.module.css';

interface QueryResultsProps {
  data: DbQueryResponse | null;
  loading: boolean;
  error: string | null;
}

type RowItem = { id: string; [col: string]: string };

const DEFAULT_PAGE_SIZE = 20;

export const QueryResults = ({ data, loading, error }: QueryResultsProps) => {
  const { t } = useTranslationRef(appTranslationRef);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  const columnConfig = useMemo<ColumnConfig<RowItem>[]>(() => {
    if (!data) return [];
    return data.columns.map((col, i) => ({
      id: `col-${i}`,
      label: col,
      ...(i === 0 ? { isRowHeader: true } : {}),
      cell: (item: RowItem) => <CellText title={item[`col-${i}`] ?? ''} />,
    }));
  }, [data]);

  const allRows = useMemo<RowItem[]>(() => {
    if (!data) return [];
    return data.rows.map((row, rowIdx) => {
      const item: RowItem = { id: String(rowIdx) };
      data.columns.forEach((_, colIdx) => {
        item[`col-${colIdx}`] = row[colIdx] ?? '';
      });
      return item;
    });
  }, [data]);

  const totalCount = allRows.length;
  const offset = page * pageSize;
  const pageData = useMemo(
    () => allRows.slice(offset, offset + pageSize),
    [allRows, offset, pageSize],
  );

  const hasNextPage = offset + pageSize < totalCount;
  const hasPreviousPage = page > 0;

  const handleNextPage = useCallback(() => setPage(p => p + 1), []);
  const handlePreviousPage = useCallback(() => setPage(p => Math.max(0, p - 1)), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

  const pageSizeOptions = useMemo(() => [
    { label: `${t('dbExplorer.results.show')} 10 ${t('dbExplorer.results.rows')}`, value: 10 },
    { label: `${t('dbExplorer.results.show')} 20 ${t('dbExplorer.results.rows')}`, value: 20 },
    { label: `${t('dbExplorer.results.show')} 50 ${t('dbExplorer.results.rows')}`, value: 50 },
    { label: `${t('dbExplorer.results.show')} 100 ${t('dbExplorer.results.rows')}`, value: 100 },
  ], [t]);

  const getLabel = useCallback(
    ({ offset: o, totalCount: tc }: { pageSize: number; offset?: number; totalCount?: number }) => {
      if (o !== undefined && tc !== undefined) {
        const from = o + 1;
        const to = Math.min(o + pageSize, tc);
        return `${from} - ${to} / ${tc}`;
      }
      return `${tc ?? 0} ${t('dbExplorer.results.rows')}`;
    },
    [pageSize, t],
  );

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        <Text variant="title-small" weight="bold">
          {t('dbExplorer.results.title')}
        </Text>
        <Text variant="body-medium" color="secondary">
          {t('dbExplorer.results.loading')}
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" gap="3">
        <Text variant="title-small" weight="bold">
          {t('dbExplorer.results.title')}
        </Text>
        <Box className={styles.errorBox}>
          <Text variant="body-small" color="danger">
            {error}
          </Text>
        </Box>
      </Flex>
    );
  }

  if (!data) return null;

  if (data.columns.length === 0) {
    return (
      <Flex direction="column" gap="3">
        <Text variant="title-small" weight="bold">
          {t('dbExplorer.results.title')}
        </Text>
        <Text variant="body-medium" color="secondary">
          {t('dbExplorer.results.noColumns')}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="3">
      <Flex justify="between" align="center">
        <Text variant="title-small" weight="bold">
          {t('dbExplorer.results.title')}
        </Text>
        <Flex gap="2" align="center">
          <Text variant="body-small" color="secondary">
            {data.rowCount} {data.rowCount === 1 ? t('dbExplorer.results.row') : t('dbExplorer.results.rows')}
          </Text>
          {data.truncated && (
            <Text variant="body-x-small" color="warning">
              {t('dbExplorer.results.truncated')}
            </Text>
          )}
        </Flex>
      </Flex>

      <Table
        columnConfig={columnConfig}
        data={pageData}
        pagination={{ type: 'none' }}
      />
      <TablePagination
        pageSize={pageSize}
        totalCount={totalCount}
        offset={offset}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
        getLabel={getLabel}
        showPageSizeOptions
      />
    </Flex>
  );
};
